import React from 'react';
import PropTypes from 'prop-types';
import { Box, Stack, Typography } from '@mui/material';
import ConfigCard from './ConfigCard';
import { ChipsBox, ChipToggle, AvailableChip, SubLabel } from './_PdiHelpers';

// Building blocks shared across all three slot lists. Each slot picks its
// own subset and ordering.
const ALL_PARTS = [
    { key: 'name',       i18nKey: 'cfg_pdi_title_part_name',       fallback: 'Name'       },
    { key: 'brand',      i18nKey: 'cfg_pdi_title_part_brand',      fallback: 'Brand'      },
    { key: 'reference',  i18nKey: 'cfg_pdi_title_part_reference',  fallback: 'Reference'  },
    { key: 'type',       i18nKey: 'cfg_pdi_title_part_type',       fallback: 'Type'       },
    { key: 'gender',     i18nKey: 'cfg_pdi_title_part_gender',     fallback: 'Gender'     },
    { key: 'subtype',    i18nKey: 'cfg_pdi_title_part_subtype',    fallback: 'Subtype'    },
    { key: 'collection', i18nKey: 'cfg_pdi_title_part_collection', fallback: 'Collection' },
    { key: 'color',      i18nKey: 'cfg_pdi_title_part_color',      fallback: 'Color'      },
];

// Defaults match the schema illustrations.
const DEFAULTS = {
    browserTab:  ['type', 'brand', 'reference'],
    pageHeading: ['name', 'reference'],
    urlSlug:     ['gender', 'type', 'reference'],
};

// Each slot stores { used: [keys in order] }.
const SlotEditor = ({ titleLabel, helpText, slotKey, slotData, defaults, tr, onChangeSlot }) => {
    const used    = Array.isArray(slotData?.used) ? slotData.used : defaults;
    const usedSet = new Set(used);
    const available = ALL_PARTS.filter(p => !usedSet.has(p.key)).map(p => p.key);

    const setUsed = (newUsed) => onChangeSlot(slotKey, { used: newUsed });

    const labelFor = (k) => {
        const meta = ALL_PARTS.find(p => p.key === k);
        return meta ? tr(meta.i18nKey, meta.fallback) : k;
    };

    return (
        <Box>
            <SubLabel>{titleLabel}</SubLabel>
            {helpText && (
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.75, mt: -0.5, lineHeight: 1.4 }}>
                    {helpText}
                </Typography>
            )}
            <ChipsBox>
                {used.map(k => (
                    <ChipToggle
                        key={k}
                        label={labelFor(k)}
                        onRemove={() => setUsed(used.filter(x => x !== k))}
                    />
                ))}
                {used.length === 0 && (
                    <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic', px: 1, py: 0.5 }}>
                        Nothing here yet — pick from the available row below.
                    </Typography>
                )}
            </ChipsBox>
            {available.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
                    {available.map(k => (
                        <AvailableChip
                            key={k}
                            label={labelFor(k)}
                            onAdd={() => setUsed([...used, k])}
                        />
                    ))}
                </Box>
            )}
        </Box>
    );
};
SlotEditor.propTypes = {
    titleLabel: PropTypes.node, helpText: PropTypes.node, slotKey: PropTypes.string,
    slotData: PropTypes.object, defaults: PropTypes.array, tr: PropTypes.func, onChangeSlot: PropTypes.func,
};

const ProductTitleAndUrlCard = ({ value = {}, onChange, theme, t }) => {
    const tr = (key, fallback) => (typeof t === 'function' ? t(key) : fallback);
    const onChangeSlot = (slotKey, slotData) => onChange && onChange(slotKey, slotData);

    return (
        <ConfigCard
            title={tr('cfg_pdi_title_url_title', 'Product title & URL')}
            description={tr('cfg_pdi_title_url_desc', 'How the product is identified across the site. Drag chips to reorder.')}
            theme={theme}
        >
            <Stack spacing={2.5}>
                <SlotEditor
                    titleLabel={tr('cfg_pdi_title_browser_tab', 'Browser tab title')}
                    helpText={tr('cfg_pdi_title_browser_tab_help', 'Shown in the browser tab and search results. Example: "Braxx (2231)"')}
                    slotKey="browserTab"
                    slotData={value.browserTab}
                    defaults={DEFAULTS.browserTab}
                    tr={tr}
                    onChangeSlot={onChangeSlot}
                />
                <SlotEditor
                    titleLabel={tr('cfg_pdi_title_page_heading', 'Page heading on the product page')}
                    helpText={tr('cfg_pdi_title_page_heading_help', 'The big title shown above the product. Example: "Garden of Eden — Dames — Broek"')}
                    slotKey="pageHeading"
                    slotData={value.pageHeading}
                    defaults={DEFAULTS.pageHeading}
                    tr={tr}
                    onChangeSlot={onChangeSlot}
                />
                <SlotEditor
                    titleLabel={tr('cfg_pdi_title_url_slug', 'Product URL slug')}
                    helpText={tr('cfg_pdi_title_url_slug_help', 'Becomes part of the web address. Example: /shop/dames/broek/braxx-2231')}
                    slotKey="urlSlug"
                    slotData={value.urlSlug}
                    defaults={DEFAULTS.urlSlug}
                    tr={tr}
                    onChangeSlot={onChangeSlot}
                />
            </Stack>
        </ConfigCard>
    );
};

ProductTitleAndUrlCard.propTypes = {
    value:    PropTypes.object,
    onChange: PropTypes.func,
    theme:    PropTypes.string,
    t:        PropTypes.func,
};

export default ProductTitleAndUrlCard;
